import {
  SearchParams,
  createUrlBuilder,
  e,
  partsToPathname,
  pathnameToParts,
} from "@ubnt/store.core/lib/utils/url";
import { SiteId } from "@ubnt/store.core/lib/contexts/MenuContext/mapping";
import { StoreId, Language } from "@ubnt/store.core/lib/types";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_STORE_ID,
  GLOBAL_SITE_CONFIG,
} from "./constants";
import { parseSiteUrl } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import {
  CategorySlug,
  PrimaryCategoryId,
} from "@ubnt/store.core/lib/constants/proStoreCategories/mapping/mapping";

type ProductLine = "amplifi" | "uisp" | "unifi";

/**
 * Memoizing the variables as the variables are injected via webpack.DefinePlugin which AFAIK just does string
 * replacement to the values, so that would make it so that each re-render creates new objects
 */

export const STORE_URLS = Object.entries(
  GLOBAL_SITE_CONFIG.store.regions,
).reduce((acc, [storeId, region]) => {
  acc[storeId as StoreId] = region.baseUrl;
  return acc;
}, {}) as Record<StoreId, string>;

/* ######################################## */
/* Lookups */
/* ######################################## */

const storeLinkBuilder = (storeId: StoreId, language: Language) =>
  createUrlBuilder(
    parseSiteUrl({
      siteUrl: [SiteId.Store, "/"],
      storeId,
      language,
      addSiteConfigReferenceSearchParams: true,
      targetSiteConfig:
        GLOBAL_SITE_CONFIG[SiteId.Store].regions[storeId] ??
        GLOBAL_SITE_CONFIG[SiteId.Store].defaultRegion,
    }),
  );
const uiComLink = (storeId: StoreId) =>
  createUrlBuilder(
    GLOBAL_SITE_CONFIG[SiteId.Ui].regions[storeId].baseUrl ??
      GLOBAL_SITE_CONFIG[SiteId.Ui].defaultRegion.baseUrl,
  );
const uispComLink = (storeId: StoreId) =>
  createUrlBuilder(
    GLOBAL_SITE_CONFIG[SiteId.Uisp].regions[storeId].baseUrl ??
      GLOBAL_SITE_CONFIG[SiteId.Uisp].defaultRegion.baseUrl,
  );
const techSpecsLink = createUrlBuilder(
  GLOBAL_SITE_CONFIG[SiteId.TechSpecs].baseUrl,
);

/* ######################################## */
/* All routes */
/* ######################################## */
export const allRoutes = (
  storeId: StoreId = DEFAULT_STORE_ID,
  language: Language = DEFAULT_LANGUAGE,
) => {
  const storeLink = storeLinkBuilder(storeId, language);

  return {
    homepage: () =>
      storeLink({
        pathname: e`/`,
      }),
    store: {
      homepage: storeLink({
        pathname: e`/`,
      }),
      category: (
        category: `${CategorySlug}`,
        searchParams?: {
          filter?: string;
        },
      ) =>
        storeLink({
          pathname: e`/category/${category}`,
          searchParams: { filter: searchParams?.filter },
        }),
      categoryProducts: ({
        category,
        productSlug,
      }: {
        category: `${CategorySlug}`;
        productSlug: string;
      }) =>
        storeLink({
          pathname: e`/category/${category}/products/${productSlug}`,
        }),
      tags: (tag: string) =>
        storeLink({
          pathname: e`/tags/${tag}`,
        }),
    },

    search: ({ searchTerm }: { searchTerm?: string }) =>
      storeLink({
        pathname: e`/search`,
        searchParams: { q: searchTerm },
      }),

    uiComPage: ({
      path,
      searchParams,
    }: {
      path: string;
      searchParams?: SearchParams;
    }) =>
      uiComLink(storeId)({
        pathname: partsToPathname([
          storeId,
          language,
          ...pathnameToParts(path),
        ]),
        searchParams,
      }),

    uispCom: () => uispComLink(storeId)({ pathname: e`/` }),

    techSpecs: ({
      path,
      searchParams,
    }: {
      path:
        | `/${ProductLine}`
        | `/${ProductLine}/${PrimaryCategoryId}`
        | `/${ProductLine}/${PrimaryCategoryId | "other"}/${string}`;
      searchParams?: {
        subcategory?: `${CategorySlug}`;
        [key: string]: string | undefined;
      };
    }) =>
      techSpecsLink({
        pathname: partsToPathname([...pathnameToParts(path)]),
        ...(searchParams && { searchParams }),
      }),
  };
};
